import React, { MouseEventHandler } from 'react';
import classNames from 'classnames';

type ButtonSize = 'x-small' | 'small' | 'medium' | 'large';
type ButtonVariant = 'primary' | 'outlined' | 'plain' | 'text';
type ColorVariant = 'primary' | 'secondary' | 'danger';

export interface ButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  /**
   * The content of the component.
   */
  children: React.ReactNode;

  /**
   * @ignore
   * Classes applied to the root element.
   */
  className?: string;

  /**
   * The component to use to render the button.
   * This is useful if you want to render an input button. Just pass a label as
   * the component type and make the child an input.
   *
   * @default "button"
   */
  component?: React.ElementType;

  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * An icon to display after the children.
   */
  endIcon?: React.ReactNode;

  /**
   * If `true`, the button will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean;

  /**
   * The callback when a user clicks on the button.
   */
  onClick?: MouseEventHandler;

  /**
   * The size of the button.
   * @default medium
   */
  size?: ButtonSize;

  /**
   * An icon to display before the children.
   */
  startIcon?: React.ReactNode;

  /**
   * The variant to use.
   * @default 'primary'
   */
  variant?: ButtonVariant;

  /**
   * The color variant to use.
   * @default 'primary'
   */
  colorVariant?: ColorVariant;
}

/**
 * Buttons are used based on their level of importance.
 * That allows us to create hierarchy in how much attention each type of button commands. In addition to type,
 * we have variations based on size for different use cases, like admin vs patient.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1784%3A982
 */
const Button = React.forwardRef(
  (
    {
      children,
      className,
      component = 'button',
      disabled = false,
      endIcon,
      fullWidth = false,
      size = 'medium',
      startIcon,
      variant = 'primary',
      colorVariant = 'primary',
      ...rest
    }: ButtonProps,
    ref,
  ) => {
    const Component = component ?? 'button';

    return (
      <Component
        className={classNames(
          'button',
          `button-${size}`,
          `button-${variant}`,
          `button-color-${colorVariant}`,
          {
            'full-width': fullWidth,
          },
          className,
        )}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        {...rest}
        ref={ref}
      >
        {startIcon && <div className="icon-wrapper">{startIcon}</div>}
        {children}
        {endIcon && <div className="icon-wrapper">{endIcon}</div>}
      </Component>
    );
  },
);

Button.displayName = 'Button';

export default Button;
